import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api/axiosInstance';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token'); // Get the token first!

    // Check if BOTH exist and are valid
    if (savedUser && savedToken) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [globalTenants, setGlobalTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. Define logout first and wrap it
  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token'); // Good practice to clear both
    setUser(null);
  }, []); // No dependencies, so this identity is now permanent


  const refreshGlobalTenants = useCallback(async () => {
    try {
      // Interceptor handles the token. No need to check !token here.
      const res = await api.get('/tenants');
      setGlobalTenants(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to refresh tenants:", err);
      setGlobalTenants([]);
    }
  }, []);


  // Restore from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      setUser(JSON.parse(stored));
    }
    setLoading(false);
  }, []);


  // Auto-load tenants if the logged-in user is a superuser
  useEffect(() => {
    if (user?.role === 'superuser') {
      refreshGlobalTenants();
    } else {
      // Clear the list if a non-superuser logs in
      setGlobalTenants([]);
    }
  }, [user, refreshGlobalTenants]);



  const login = async (username, password) => {
    try {
      const res = await api.post('/auth/login', { username, password });
      const data = res.data;

      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setUser(data.user);

        let redirectPath = (data.user.role === 'superuser' && !data.user.tenant_id)
          ? '/admin/tenants'
          : '/';

        if (data.user.requiresPasswordChange) {
            redirectPath = '/force-password-change';
        }

        return { success: true, redirectTo: redirectPath };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      const message = error.response?.data?.message || 'Unable to connect to the server.';
      return { success: false, message };
    }
  };


  const switchTenant = useCallback(async (tenantId) => {
    try {
      const res = await api.post('/auth/switch-context', { tenantId });
      const data = res.data;

      const decoded = jwtDecode(data.token);

      const updatedUser = {
        ...JSON.parse(localStorage.getItem('user')),
        role: decoded.role,
        tenant_id: decoded.tenant_id,
        tenant_name: data.tenant_name,
        schema_name: decoded.schema_name,
        enabled_modules: data.enabled_modules || []
      };

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return { success: true, target: decoded.tenant_id ? '/' : '/admin/tenants' };
    } catch (err) {
      console.error("Switch failed", err);
      return { success: false };
    }
  }, [setUser]);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      login,
      logout,
      loading,
      globalTenants,
      refreshGlobalTenants,
      switchTenant
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);